import { useQuery } from "@tanstack/react-query";
import { type FC, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Headline, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  getEvalMultiModelTest,
  getGetEvalMultiModelTestQueryKey,
  useBasecampOrganizationsRetrieve,
  useListEvalModelComparisons,
  useListEvalMultiModelTests,
} from "@api/rest/types-and-hooks";
import { ComparisonSetupCard } from "@components/ModelTesting/ComparisonSetupCard";
import { MultiModelTestSetupCard } from "@components/ModelTesting/MultiModelTestSetupCard";
import {
  ModelTestingView,
  type ModelTestingViewValue,
  VERDICT_LABELS,
  Verdict,
  type VerdictValue,
  modelLabel,
  scoreTone,
} from "@components/ModelTesting/constants";

// Decided verdicts carry the design system's positive/negative pairing; undecided stays flat.
const VERDICT_CHIP_CLASSES: Record<VerdictValue, string> = {
  [Verdict.Undecided]: "bg-bg-default text-text-secondary",
  [Verdict.SafeToSwitch]: "bg-green-100 text-green-700",
  [Verdict.NotSafe]: "bg-red-100 text-red-600",
};

/** The expanded body of a past-test row: its individual template × model
 *  results, inline. Fetched on expand (a completed test's detail GET is
 *  a cheap read); each cell opens its comparison in queue mode. */
const MultiTestCells: FC<{ testId: number; onOpenCell: (comparisonId: number) => void }> = ({ testId, onOpenCell }) => {
  const { data: detail } = useQuery({
    queryKey: getGetEvalMultiModelTestQueryKey(testId),
    queryFn: async () => {
      const resp = await getEvalMultiModelTest(testId);
      return resp.status === 200 ? resp.data : null;
    },
  });
  if (!detail) return <p className="px-2 py-2 text-xs text-text-tertiary">Loading results…</p>;
  return (
    <div className="flex flex-col py-1">
      {detail.cells.map((cell) => {
        const summary = cell.score_summary;
        const inFlight = cell.progress.pending > 0 || cell.progress.running > 0;
        return (
          <button
            key={cell.comparison_id}
            type="button"
            onClick={() => onOpenCell(cell.comparison_id)}
            className="flex items-center justify-between gap-4 rounded px-2 py-1.5 text-left transition-colors hover:bg-bg-default"
          >
            <span className="min-w-0 truncate">
              <span className="text-sm font-medium text-text-primary">{cell.template_name}</span>
              <span className="ml-2 text-xs text-text-secondary" title={cell.candidate_model}>
                {modelLabel(cell.candidate_model)}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-2">
              {inFlight ? (
                <span className="text-xs text-text-tertiary">
                  {cell.progress.complete}/{cell.progress.total} runs
                </span>
              ) : summary.scored > 0 ? (
                <>
                  <span
                    className={`rounded px-1.5 py-0.5 text-xs font-semibold ${scoreTone(summary)}`}
                    title="Pass rate: fields the judge called same or better"
                  >
                    {Math.round((summary.average ?? 0) * 100)}% pass
                  </span>
                  {summary.flagged > 0 ? (
                    <span className="text-xs font-medium text-red-600">{summary.flagged} to review</span>
                  ) : null}
                </>
              ) : (
                <span className="text-xs text-text-tertiary">not judged yet</span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const formatWhen = (value: string): string =>
  new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

/** Model Testing landing: two tabs with different scopes. Multi-model tests *  spans every org's templates at once (pick the next model); Comparison
 *  is a single template on one candidate, filtered by org. */
export const ModelTestingTab: FC = () => {
  const navigate = useNavigate();
  // The active tab lives in the URL so coming back from a results page
  // (or refreshing) lands on the tab you were on.
  const [searchParams, setSearchParams] = useSearchParams();
  const view: ModelTestingViewValue =
    searchParams.get("tab") === ModelTestingView.Multi ? ModelTestingView.Multi : ModelTestingView.Comparison;
  const setView = (next: ModelTestingViewValue) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("tab", next);
        return params;
      },
      { replace: true },
    );
  const [orgId, setOrgId] = useState<number | null>(null);
  const [expandedMultiTests, setExpandedMultiTests] = useState<Set<number>>(new Set());
  const toggleMultiTestExpanded = (id: number) =>
    setExpandedMultiTests((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const openComparison = (id: number) => navigate(`/model-testing/comparisons/${id}`);
  const openMultiTest = (id: number) => navigate(`/model-testing/multi/${id}`);

  const { data: orgsResponse } = useBasecampOrganizationsRetrieve({});
  const orgs = useMemo(
    () =>
      orgsResponse?.status === 200 ? (Array.isArray(orgsResponse.data) ? orgsResponse.data : [orgsResponse.data]) : [],
    [orgsResponse],
  );

  const { data: comparisonsResponse } = useListEvalModelComparisons(
    orgId !== null ? { organization_id: orgId } : undefined,
  );
  const comparisons = (comparisonsResponse?.status === 200 ? comparisonsResponse.data.results : null) ?? [];

  const { data: multiTestsResponse } = useListEvalMultiModelTests();
  const multiTests = (multiTestsResponse?.status === 200 ? multiTestsResponse.data.results : null) ?? [];

  return (
    <div className="flex flex-col gap-y-6">
      <div>
        <Headline size="medium">Model testing</Headline>
        <div className="mt-3 flex gap-1.5">
          {(
            [
              [ModelTestingView.Comparison, "Single comparison"],
              [ModelTestingView.Multi, "Multi-model testing"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setView(value)}
              className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
                view === value
                  ? "border-limestone-900 bg-bg-surface font-semibold text-text-primary"
                  : "border-border-subtle bg-bg-default text-text-secondary hover:bg-bg-surface"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {view === ModelTestingView.Comparison ? (
        <ComparisonSetupCard orgs={orgs} orgId={orgId} onOrgChange={setOrgId} onStarted={openComparison} />
      ) : (
        <MultiModelTestSetupCard onStarted={openMultiTest} />
      )}

      {view === ModelTestingView.Multi && multiTests.length > 0 ? (
        <section className="rounded-lg border border-border-subtle bg-bg-surface p-4">
          <Label size="small" colorType="secondary" className="uppercase tracking-wide">
            Past multi-model tests
          </Label>
          {/* Bounded height — these lists only grow; the page shouldn't. */}
          <div className="mt-3 max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-bg-surface">
                <tr className="text-left text-xs text-text-tertiary">
                  <th className="w-8 pb-2 font-normal" aria-label="Expand" />
                  <th className="pb-2 pr-6 font-normal">Name</th>
                  <th className="pb-2 pr-6 font-normal">Candidates</th>
                  <th className="pb-2 pr-6 font-normal">Templates</th>
                  <th className="whitespace-nowrap pb-2 font-normal">When</th>
                </tr>
              </thead>
              <tbody>
                {multiTests.flatMap((test) => [
                  <tr
                    key={test.id}
                    onClick={() => openMultiTest(test.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") openMultiTest(test.id);
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Open test ${test.name || test.id}`}
                    className="cursor-pointer border-t border-border-subtle transition-colors hover:bg-bg-default focus:outline-none focus-visible:bg-bg-default"
                  >
                    {/* The whole cell toggles — a glyph-sized target inside a
                        clickable row means near-misses navigate instead. */}
                    <td
                      className="align-middle"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMultiTestExpanded(test.id);
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        aria-label={expandedMultiTests.has(test.id) ? "Collapse results" : "Show individual results"}
                        aria-expanded={expandedMultiTests.has(test.id)}
                        className="w-full rounded px-2 py-3 text-xs text-text-tertiary transition-colors hover:bg-bg-surface hover:text-text-primary"
                      >
                        {expandedMultiTests.has(test.id) ? "▾" : "▸"}
                      </button>
                    </td>
                    <td className="py-3 pr-6 align-middle font-medium text-text-primary">
                      {test.name || `Test #${test.id}`}
                    </td>
                    <td className="py-3 pr-6 align-middle text-xs text-text-secondary">
                      {test.candidate_models.map(modelLabel).join(", ")}
                    </td>
                    <td className="py-3 pr-6 align-middle text-text-secondary">{test.template_count}</td>
                    <td className="whitespace-nowrap py-3 align-middle text-text-tertiary">
                      {formatWhen(test.created_date)}
                    </td>
                  </tr>,
                  ...(expandedMultiTests.has(test.id)
                    ? [
                        <tr key={`${test.id}-cells`} className="border-t border-border-subtle bg-bg-default">
                          <td />
                          <td colSpan={4} className="py-1">
                            <MultiTestCells
                              testId={test.id}
                              onOpenCell={(comparisonId) =>
                                navigate(`/model-testing/comparisons/${comparisonId}?multi=${test.id}`)
                              }
                            />
                          </td>
                        </tr>,
                      ]
                    : []),
                ])}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {view === ModelTestingView.Comparison ? (
        <section className="rounded-lg border border-border-subtle bg-bg-surface p-4">
          <Label size="small" colorType="secondary" className="uppercase tracking-wide">
            Past comparisons
          </Label>
          {comparisons.length === 0 ? (
            <Paragraph displayType="normal" size="medium" className="mt-3 text-text-secondary">
              {orgId === null ? "No comparisons yet — run one above." : "No comparisons for this organization yet."}
            </Paragraph>
          ) : (
            // Bounded height — these lists only grow; the page shouldn't.
            <div className="mt-3 max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-bg-surface">
                  <tr className="text-left text-xs text-text-tertiary">
                    <th className="pb-2 pr-6 font-normal">Template</th>
                    <th className="pb-2 pr-6 font-normal">Candidate</th>
                    <th className="pb-2 pr-6 font-normal">Pass rate</th>
                    <th className="pb-2 pr-6 font-normal">Verdict</th>
                    <th className="whitespace-nowrap pb-2 font-normal">When</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisons.map((comparison) => (
                    // The row is the button: click anywhere on it to open.
                    <tr
                      key={comparison.id}
                      onClick={() => openComparison(comparison.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") openComparison(comparison.id);
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={`Open comparison for ${comparison.template_name}`}
                      className="cursor-pointer border-t border-border-subtle transition-colors hover:bg-bg-default focus:outline-none focus-visible:bg-bg-default"
                    >
                      <td className="py-3 pr-6 align-middle font-medium text-text-primary">
                        {comparison.template_name}
                      </td>
                      <td className="whitespace-nowrap py-3 pr-6 align-middle">
                        {/* The baseline is (almost) always the default — the
                        candidate is the news. */}
                        <span className="text-sm font-medium text-text-primary" title={comparison.candidate_model}>
                          {modelLabel(comparison.candidate_model)}
                        </span>
                        <span className="ml-2 text-xs text-text-tertiary">
                          vs {modelLabel(comparison.baseline_model)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap py-3 pr-6 align-middle">
                        {comparison.score_summary.scored > 0 ? (
                          <>
                            <span
                              className={`rounded px-2 py-0.5 text-xs font-medium ${scoreTone(comparison.score_summary)}`}
                              title={`${comparison.score_summary.passed} of ${comparison.score_summary.total} fields judged same or better`}
                            >
                              {Math.round((comparison.score_summary.average ?? 0) * 100)}%
                            </span>
                            {comparison.score_summary.flagged > 0 ? (
                              <span className="ml-2 text-xs font-medium text-red-600">
                                {comparison.score_summary.flagged} to review
                              </span>
                            ) : null}
                          </>
                        ) : (
                          <span className="text-xs text-text-tertiary">—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap py-3 pr-6 align-middle">
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-medium ${
                            VERDICT_CHIP_CLASSES[comparison.verdict as VerdictValue] ??
                            VERDICT_CHIP_CLASSES[Verdict.Undecided]
                          }`}
                        >
                          {VERDICT_LABELS[comparison.verdict as VerdictValue] ?? comparison.verdict}
                        </span>
                      </td>
                      <td className="whitespace-nowrap py-3 align-middle text-text-tertiary">
                        {formatWhen(comparison.created_date)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
};
